import { P as PubSub } from './pubsub-CoPYzvVm.js';
import { N as Notyf } from './notyf-DIw0RvBj.js';

class QuantityInput extends HTMLElement {
  #input = this.querySelector('input');
  #buttons = this.querySelectorAll('button');

  connectedCallback() {
    this.#buttons.forEach((button) => {
      button.addEventListener('click', (event) => this.#handleStep(event.currentTarget.name));
    });

    this.#input.addEventListener('change', () => {
      this.#constrainAndUpdateInput(this.#input.value);
      this.#publishQuantityChange();
    });
  }

  /**
   * Handle the step up or step down of the quantity
   * @param {string} type - The type of step to be taken
   */
  #handleStep(type) {
    const value = parseInt(this.#input.value);
    const step = parseInt(this.#input.step) || 1;
    const newValue = type === 'minus' ? Math.max(value - step, 0) : value + step;
    const updatedValue = this.#constrainAndUpdateInput(newValue);

    if (parseInt(updatedValue) !== parseInt(value)) this.#publishQuantityChange();
  }

  /**
   * Constrain and update the input.
   * @param {number} newValue - The new value.
   * @returns {number} The constrained value.
   */
  #constrainAndUpdateInput(newValue) {
    const { min, max } = this.#input;
    const maxOrderAmount = Number(this.getAttribute('data-max-order-amount'));
    const oldLineQty = Number(this.#input.value) || 0;

    this.#input.value = newValue;
    this.#enableAllButtons();

    if (maxOrderAmount > 0) {
      this.#applyMaxOrderConstraint(maxOrderAmount, oldLineQty, min, newValue);
    } else {
      this.#applyInventoryConstraint(newValue, min, max);
    }

    return this.#input.value;
  }

  #enableAllButtons() {
    this.#buttons.forEach((button) => {
      button.disabled = false;
    });
  }

  #applyMaxOrderConstraint(maxOrder, oldLineQty, min, newValue) {
    const productCartQuantity = this.dataset.productCartQuantity;

    if (productCartQuantity !== undefined && productCartQuantity !== '') {
      const cartQty = Number(productCartQuantity);

      if (this.dataset.itemIndex !== undefined) {
        this.#applyCartLineMaxOrder(maxOrder, cartQty, oldLineQty, min, newValue);
      } else {
        this.#applyProductPageMaxOrder(maxOrder, cartQty, min, newValue);
      }
      return;
    }

    if (Number(newValue) > maxOrder) {
      this.#input.value = maxOrder;
      this.#disablePlusButton();
      return;
    }

    if (Number(this.#input.value) >= maxOrder) {
      this.#disablePlusButton();
    }
  }

  #applyCartLineMaxOrder(maxOrder, cartQty, oldLineQty, min, newValue) {
    const otherLinesQty = cartQty - oldLineQty;
    const maxLineQty = maxOrder - otherLinesQty;

    if (Number(newValue) > maxLineQty) {
      this.#input.value = Math.max(maxLineQty, Number(min) || 0);
    }

    if (otherLinesQty + Number(this.#input.value) >= maxOrder) {
      this.#disablePlusButton();
    }
  }

  #applyProductPageMaxOrder(maxOrder, cartQty, min, newValue) {
    const maxSelectorQty = maxOrder - cartQty;

    if (Number(newValue) > maxSelectorQty) {
      this.#input.value = Math.max(maxSelectorQty, Number(min) || 1);
    }

    if (cartQty + Number(this.#input.value) >= maxOrder) {
      this.#disablePlusButton();
    }
  }

  #applyInventoryConstraint(newValue, min, max) {
    if (newValue >= max && max !== '') {
      Notyf.error(window.toast_translations.product.quantity.error_max.replace('{{ max }}', max));
      this.#input.value = max;
      return;
    }

    if (newValue <= min) {
      this.#input.value = min;
    }
  }

  #publishQuantityChange() {
    PubSub.publish('quantity:change', {
      line: this.dataset?.itemIndex,
      quantity: this.#input.value,
    });
  }

  #disablePlusButton() {
    this.#buttons.forEach((button) => {
      if (button.name === 'plus') button.disabled = true;
    });
  }

  /**
   * @description Sync max-order UI after cart changes on the product page.
   * @param {{ maxReached: boolean, productCartQuantity: number }} state
   */
  setMaxOrderState({ maxReached, productCartQuantity }) {
    if (this.dataset.itemIndex !== undefined) return;

    this.dataset.productCartQuantity = productCartQuantity;

    const inventoryMax = this.#input.max !== '' ? Number(this.#input.max) : null;
    const value = Number(this.#input.value);
    const disablePlus =
      maxReached || (inventoryMax !== null && !Number.isNaN(inventoryMax) && value >= inventoryMax);

    this.#buttons.forEach((button) => {
      if (button.name === 'plus') button.disabled = disablePlus;
    });
  }
}

customElements.define('quantity-input', QuantityInput);
